import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CCard, CCardBody, CButton, CSpinner } from '@coreui/react';
import { FaMicrophone, FaStop } from 'react-icons/fa';
import { postVoicePreview } from 'src/repositories/voicePreviewRepository';
import { toast } from 'react-toastify';
import { CONFIG } from 'src/config/envConstants';
import './VoicePreviewPanel.css';
import { DEFAULT_VOICE_AI_MODEL, VOICE_PREVIEW_MODEL_OPTIONS } from './aiVoiceModels';

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

const VoicePreviewPanel = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [translation, setTranslation] = useState('');
  const [detectedLang, setDetectedLang] = useState('');
  const [isEnglishResult, setIsEnglishResult] = useState(null);
  const [previewModel, setPreviewModel] = useState(DEFAULT_VOICE_AI_MODEL);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const flowRef = useRef(null);
  const svgRef = useRef(null);
  const pathModelToMicRef = useRef(null);
  const pathMicToTranscriptionRef = useRef(null);
  const pathTranscriptionToAudioRef = useRef(null);
  const modelNodeRefs = useRef({});
  const micNodeRef = useRef(null);
  const transcriptionNodeRef = useRef(null);
  const audioNodeRef = useRef(null);
  /** Bump when model changes so in-flight /api/process cannot repopulate stale UI. */
  const requestEpochRef = useRef(0);
  /** When true, next `onstop` discards blob (e.g. model changed mid-recording). */
  const suppressNextRecordingStopRef = useRef(false);

  const setModelNodeRef = (id) => (el) => {
    modelNodeRefs.current[id] = el;
  };

  const configured = Boolean(CONFIG.REACT_APP_IA_DATA_COLLECTOR_API_BASE_URL);

  /** Shown only after user stops recording (then processing + results + audio). */
  const showTranscriptionAndAudio =
    !isRecording &&
    (isProcessing ||
      Boolean(recordedUrl) ||
      Boolean(transcript) ||
      Boolean(translation) ||
      Boolean(detectedLang));

  const curve = (x1, y1, x2, y2) => {
    const midX = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
  };

  const drawWires = useCallback(() => {
    const flow = flowRef.current;
    const svg = svgRef.current;
    const p0 = pathModelToMicRef.current;
    const p1 = pathMicToTranscriptionRef.current;
    const p2 = pathTranscriptionToAudioRef.current;
    if (!flow || !svg || !p0) return;

    const w = flow.offsetWidth;
    const h = flow.offsetHeight;
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);

    const fr = flow.getBoundingClientRect();
    const modelEl = modelNodeRefs.current[previewModel];
    const micEl = micNodeRef.current;
    const transEl = transcriptionNodeRef.current;
    const audioEl = audioNodeRef.current;

    if (modelEl && micEl) {
      const micr = micEl.getBoundingClientRect();
      const pillEl = modelEl.querySelector('.voice-preview-panel__model-option');
      const source = pillEl ? pillEl.getBoundingClientRect() : modelEl.getBoundingClientRect();
      const x1 = source.right - fr.left;
      const y1 = source.top + source.height / 2 - fr.top;
      const x2 = micr.left - fr.left;
      const y2 = micr.top + micr.height / 2 - fr.top;
      p0.setAttribute('d', curve(x1, y1, x2, y2));
      p0.style.opacity = '1';
    } else {
      p0.style.opacity = '0';
    }

    /* Mic → Original audio (audio column before transcription) */
    if (p1 && micEl && audioEl) {
      const micr = micEl.getBoundingClientRect();
      const ar = audioEl.getBoundingClientRect();
      const xa = micr.right - fr.left;
      const ya = micr.top + micr.height / 2 - fr.top;
      const xb = ar.left - fr.left;
      const yb = ar.top + ar.height / 2 - fr.top;
      p1.setAttribute('d', curve(xa, ya, xb, yb));
      p1.style.opacity = '1';
    } else if (p1) {
      p1.style.opacity = '0';
    }

    /* Original audio → Transcription */
    if (p2 && audioEl && transEl) {
      const ar = audioEl.getBoundingClientRect();
      const tr = transEl.getBoundingClientRect();
      const xa = ar.right - fr.left;
      const ya = ar.top + ar.height / 2 - fr.top;
      const xb = tr.left - fr.left;
      const yb = tr.top + tr.height / 2 - fr.top;
      p2.setAttribute('d', curve(xa, ya, xb, yb));
      p2.style.opacity = '1';
    } else if (p2) {
      p2.style.opacity = '0';
    }
  }, [
    previewModel,
    showTranscriptionAndAudio,
    isRecording,
    isProcessing,
    recordedUrl,
    transcript,
    translation,
    detectedLang,
  ]);

  useLayoutEffect(() => {
    drawWires();
    const flow = flowRef.current;
    const onScroll = () => drawWires();
    const ro = new ResizeObserver(() => drawWires());
    if (flow) {
      ro.observe(flow);
      flow.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', drawWires);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', drawWires);
      if (flow) flow.removeEventListener('scroll', onScroll);
    };
  }, [drawWires]);

  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((tr) => tr.stop());
        mediaStreamRef.current = null;
      }
    };
  }, [recordedUrl]);

  const stopStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((tr) => tr.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const processRecording = useCallback(async (blob, modelId) => {
    if (!blob || blob.size === 0) {
      toast.error('No audio captured. Try again.');
      return;
    }
    const epochAtStart = requestEpochRef.current;
    setIsProcessing(true);
    setTranscript('');
    setTranslation('');
    setDetectedLang('');
    setIsEnglishResult(null);
    try {
      const data = await postVoicePreview(blob, modelId);
      if (epochAtStart !== requestEpochRef.current) return;
      setTranscript(data.transcript || '');
      setTranslation(data.translation || '');
      setDetectedLang(data.detectedLang || '');
      setIsEnglishResult(typeof data.isEnglish === 'boolean' ? data.isEnglish : null);
      if (!data.transcript) {
        toast.warning('No transcript returned.');
      }
    } catch (e) {
      if (epochAtStart !== requestEpochRef.current) return;
      toast.error(typeof e?.message === 'string' ? e.message : 'Could not process audio.');
    } finally {
      if (epochAtStart === requestEpochRef.current) {
        setIsProcessing(false);
      }
    }
  }, []);

  const handleModelSelect = (id) => {
    if (id === previewModel) return;
    requestEpochRef.current += 1;

    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedUrl(null);
    setTranscript('');
    setTranslation('');
    setDetectedLang('');
    setIsEnglishResult(null);
    setIsProcessing(false);

    if (isRecording) {
      suppressNextRecordingStopRef.current = true;
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== 'inactive') rec.stop();
      } catch {
        /* ignore */
      }
      stopStream();
      chunksRef.current = [];
      setIsRecording(false);
    }

    setPreviewModel(id);
  };

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      return;
    }
    rec.stop();
    setIsRecording(false);
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone is not supported in this browser.');
      return;
    }

    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
      setRecordedUrl(null);
    }
    setTranscript('');
    setTranslation('');
    setDetectedLang('');
    setIsEnglishResult(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      rec.onstop = () => {
        if (suppressNextRecordingStopRef.current) {
          suppressNextRecordingStopRef.current = false;
          stopStream();
          chunksRef.current = [];
          return;
        }
        stopStream();
        const type = rec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        const url = URL.createObjectURL(blob);
        const modelForThisClip = previewModel;
        setRecordedUrl(url);
        processRecording(blob, modelForThisClip);
      };

      rec.start(200);
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      toast.error('Microphone permission denied or unavailable.');
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const modelDisabled = isRecording || isProcessing;
  const showResultUI =
    !isProcessing &&
    (Boolean(transcript) ||
      Boolean(translation) ||
      Boolean(detectedLang) ||
      Boolean(recordedUrl));

  return (
    <CCard className="ccard voice-preview-panel">
      <CCardBody>
        <h5 className="voice-preview-panel__title">Verify multilingual support</h5>
        <p className="voice-preview-panel__intro">
          Tap the mic and speak. After you stop, transcription and your clip appear
          with the flow below.
        </p>

        <div className="voice-preview-panel__flow" ref={flowRef}>
          <svg
            className="voice-preview-panel__wires"
            ref={svgRef}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              ref={pathModelToMicRef}
              className="voice-preview-panel__wire voice-preview-panel__wire--primary"
            />
            <path
              ref={pathMicToTranscriptionRef}
              className="voice-preview-panel__wire voice-preview-panel__wire--secondary"
            />
            <path
              ref={pathTranscriptionToAudioRef}
              className="voice-preview-panel__wire voice-preview-panel__wire--tertiary"
            />
          </svg>

          <div className="voice-preview-panel__flow-inner" role="group" aria-label="Voice preview flow">
            <div className="voice-preview-panel__stage voice-preview-panel__stage--models">
              <span className="voice-preview-panel__stage-eyebrow">Models</span>
              <div className="voice-preview-panel__model-stack">
                {VOICE_PREVIEW_MODEL_OPTIONS.map((opt) => (
                  <div
                    key={opt.id}
                    ref={setModelNodeRef(opt.id)}
                    className={`voice-preview-panel__model-node ${
                      previewModel === opt.id ? 'voice-preview-panel__model-node--active' : ''
                    }`}
                  >
                    <label
                      className={`voice-preview-panel__model-option ${
                        previewModel === opt.id ? 'voice-preview-panel__model-option--active' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="voice-preview-model"
                        value={opt.id}
                        checked={previewModel === opt.id}
                        onChange={() => handleModelSelect(opt.id)}
                        disabled={modelDisabled}
                      />
                      <span className="voice-preview-panel__model-option-label">
                        {opt.uiLabel || opt.label}
                      </span>
                      {previewModel === opt.id ? (
                        <span
                          className="voice-preview-panel__node-port voice-preview-panel__node-port--pill-end"
                          aria-hidden
                        />
                      ) : null}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="voice-preview-panel__flow-gap" aria-hidden />

            <div
              className="voice-preview-panel__mic-only-column voice-preview-panel__mic-node"
              ref={micNodeRef}
            >
              <span className="voice-preview-panel__stage-eyebrow">Record</span>
              {showTranscriptionAndAudio && isProcessing ? (
                <div className="voice-preview-panel__mic-post-capture-wrap">
                  <div className="voice-preview-panel__mic-post-capture" aria-live="polite">
                    <span className="voice-preview-panel__mic-post-capture-icon" aria-hidden>
                      ✓
                    </span>
                    <span className="voice-preview-panel__mic-post-capture-text">Processing…</span>
                  </div>
                </div>
              ) : showTranscriptionAndAudio && !isProcessing ? (
                <div className="voice-preview-panel__mic-post-capture-wrap">
                  <div className="voice-preview-panel__mic-post-capture">
                    <span className="voice-preview-panel__mic-post-capture-icon" aria-hidden>
                      ✓
                    </span>
                    <span className="voice-preview-panel__mic-post-capture-text">Captured</span>
                  </div>
                  <CButton
                    type="button"
                    color="secondary"
                    size="sm"
                    className="voice-preview-panel__record-again voice-preview-panel__record-again--mic"
                    onClick={startRecording}
                    disabled={!configured || isRecording}
                  >
                    Record again
                  </CButton>
                </div>
              ) : (
                <div className="voice-preview-panel__mic-only-body">
                  <CButton
                    type="button"
                    color={isRecording ? 'danger' : 'primary'}
                    onClick={handleMicClick}
                    disabled={!configured}
                    className="voice-preview-panel__mic-btn"
                    aria-label={isRecording ? 'Stop recording' : 'Start recording'}
                  >
                    {isRecording ? <FaStop size={18} /> : <FaMicrophone size={22} />}
                  </CButton>
                  {isRecording ? (
                    <span className="voice-preview-panel__recording-hint">Stop when done</span>
                  ) : (
                    <span className="voice-preview-panel__center-record-hint">
                      Tap to speak
                    </span>
                  )}
                </div>
              )}
              <span className="voice-preview-panel__node-port voice-preview-panel__node-port--right" aria-hidden />
            </div>

            {showTranscriptionAndAudio && (
              <>
                <div className="voice-preview-panel__flow-gap" aria-hidden />

                <div className="voice-preview-panel__audio-card" ref={audioNodeRef}>
                  <span className="voice-preview-panel__node-port voice-preview-panel__node-port--left" aria-hidden />
                  <span className="voice-preview-panel__node-tag voice-preview-panel__node-tag--dark">
                    Original audio
                  </span>
                  <div className="voice-preview-panel__audio-card-inner">
                    {recordedUrl ? (
                      <div className="voice-preview-panel__audio-shell">
                        <audio controls src={recordedUrl} className="voice-preview-panel__audio-inline" />
                      </div>
                    ) : (
                      <div className="voice-preview-panel__audio-placeholder">
                        <span className="voice-preview-panel__audio-placeholder-icon" aria-hidden>
                          ◯
                        </span>
                        <p className="voice-preview-panel__audio-placeholder-text">
                          Clip loads after capture
                        </p>
                      </div>
                    )}
                  </div>
                  <span className="voice-preview-panel__node-port voice-preview-panel__node-port--right" aria-hidden />
                </div>

                <div className="voice-preview-panel__flow-gap" aria-hidden />

                <div
                  className="voice-preview-panel__center-card voice-preview-panel__transcription-node"
                  ref={transcriptionNodeRef}
                >
                  <span className="voice-preview-panel__node-tag">Transcription</span>
                  <div className="voice-preview-panel__center-card-body">
                    {isProcessing ? (
                      <div className="voice-preview-panel__center-processing">
                        <div className="voice-preview-panel__mic-loader" role="status">
                          <div className="voice-preview-panel__mic-loader-orbit">
                            <CSpinner size="sm" className="voice-preview-panel__mic-loader-spinner" />
                          </div>
                          <span className="voice-preview-panel__mic-loader-text">Working…</span>
                        </div>
                        <div className="voice-preview-panel__center-skeletons">
                          <div className="voice-preview-panel__skeleton-line voice-preview-panel__skeleton-line--wave" />
                          <div className="voice-preview-panel__skeleton-line voice-preview-panel__skeleton-line--wave voice-preview-panel__skeleton-line--delay" />
                        </div>
                      </div>
                    ) : showResultUI ? (
                      <div className="voice-preview-panel__center-results">
                        <div className="voice-preview-panel__result-block">
                          <div className="voice-preview-panel__result-label">Original</div>
                          <p className="voice-preview-panel__result-copy">
                            {transcript ? (
                              <span key={transcript} className="voice-preview-panel__output-anim">
                                {transcript}
                              </span>
                            ) : (
                              <span className="voice-preview-panel__placeholder">—</span>
                            )}
                          </p>
                        </div>
                        <div className="voice-preview-panel__result-block">
                          <div className="voice-preview-panel__result-label">English</div>
                          <p className="voice-preview-panel__result-copy">
                            {translation ? (
                              <span key={translation} className="voice-preview-panel__output-anim">
                                {translation}
                              </span>
                            ) : isEnglishResult === true && transcript ? (
                              <span className="voice-preview-panel__note-inline">
                                Same as original (English detected)
                              </span>
                            ) : (
                              <span className="voice-preview-panel__placeholder">—</span>
                            )}
                          </p>
                        </div>
                        {detectedLang ? (
                          <div className="voice-preview-panel__lang-pills" aria-label="Detected language">
                            <span className="voice-preview-panel__lang-pill voice-preview-panel__lang-pill--active">
                              {detectedLang}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </CCardBody>
    </CCard>
  );
};

export default VoicePreviewPanel;
